import { Controller } from "@hotwired/stimulus"
import { getItem, setItem } from "../utils/local_storage"

export default class extends Controller {
  static targets = ["video", "frame", "completeButton", "completeLabel", "status", "cue", "noCues"]
  static values = { site: String, record: String }

  connect() {
    const stored = getItem(this.key, {})
    this.state = stored && typeof stored === "object" && !Array.isArray(stored) ? stored : {}
    this.lastSaved = -5
    this.syncComplete()
    if (this.hasVideoTarget && this.videoTarget.readyState >= 1) this.restore()
  }

  get key() { return `devgab:signal:${this.siteValue}:video:${this.recordValue}` }

  disconnect() { this.beforeCache() }

  restore() {
    const video = this.videoTarget
    const position = Number(this.state.position)
    if (!this.state.complete && Number.isFinite(position) && position > 0 && Number.isFinite(video.duration) && position < video.duration - 3) {
      video.currentTime = position
      this.say("Picked up where you left off.")
    }
  }

  timeUpdate() {
    const time = this.videoTarget.currentTime
    this.cueTargets.forEach(cue => cue.classList.toggle("active", time >= Number(cue.dataset.seconds) && time < Number(cue.dataset.endSeconds)))
    if (Math.abs(time - this.lastSaved) >= 5) this.save()
  }

  save() {
    if (!this.hasVideoTarget || !this.state) return
    const position = this.videoTarget.currentTime
    if (!Number.isFinite(position)) return
    this.state.position = position
    this.lastSaved = position
    setItem(this.key, this.state)
  }

  seek(event) {
    const seconds = Number(event.currentTarget.dataset.seconds)
    if (!Number.isFinite(seconds) || seconds < 0) return
    if (this.hasVideoTarget) {
      const video = this.videoTarget
      video.currentTime = Number.isFinite(video.duration) ? Math.min(seconds, video.duration) : seconds
      const play = video.play()
      play?.catch(() => this.say("Press play to continue from this moment."))
      video.scrollIntoView?.({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" })
    } else if (this.hasFrameTarget) {
      const url = new URL(this.frameTarget.src)
      if (this.frameTarget.dataset.provider === "youtube") url.searchParams.set("start", String(Math.floor(seconds)))
      else if (this.frameTarget.dataset.provider === "vimeo") url.hash = `t=${Math.floor(seconds)}s`
      this.frameTarget.src = url.href
      this.say("The player is ready at that moment. Press play to continue.")
    }
  }

  toggleComplete() {
    this.state.complete = !this.state.complete
    const saved = setItem(this.key, this.state)
    this.syncComplete()
    this.say(saved ? (this.state.complete ? "Completed. Saved on this device." : "Marked as unwatched.") : "Updated for this visit. Your browser could not save it.")
    this.dispatch("progress")
  }

  ended() {
    this.state.complete = true
    this.save()
    this.syncComplete()
    this.dispatch("progress")
    this.say("Chapter complete.")
  }

  syncComplete() {
    if (this.hasCompleteButtonTarget) {
      this.completeButtonTarget.setAttribute("aria-pressed", String(!!this.state.complete))
      this.completeButtonTarget.classList.toggle("is-complete", !!this.state.complete)
    }
    if (this.hasCompleteLabelTarget) this.completeLabelTarget.textContent = this.state.complete ? "Completed" : "Mark complete"
  }

  filterTranscript(event) {
    const query = event.target.value.trim().toLocaleLowerCase()
    this.cueTargets.forEach(cue => { cue.hidden = !cue.textContent.toLocaleLowerCase().includes(query) })
    if (this.hasNoCuesTarget) this.noCuesTarget.hidden = this.cueTargets.some(cue => !cue.hidden)
  }

  say(text) { if (this.hasStatusTarget) this.statusTarget.textContent = text }

  beforeCache() {
    this.save()
    if (this.hasVideoTarget) this.videoTarget.pause()
    if (this.hasFrameTarget) {
      // Reassigning the source stops playback before Turbo snapshots the page.
      const src = this.frameTarget.getAttribute("src")
      if (this.element.isConnected && src) this.frameTarget.setAttribute("src", src)
    }
  }
}
